import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Upload,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Target,
  Package,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import catalog from "@/api/catalog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AcceptedFileList from "@/components/AcceptedFileList";
import CoverageMatrix from "@/components/CoverageMatrix";
import UploadInspectModal from "@/components/UploadInspectModal";
import TE350ActivityReviewDialog from "@/pages/admin/_employees-canonical/TE350ActivityReviewDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const REPORT_TYPES = [
  { value: "GENDETAILED_PROD", label: "GENDETAILED_PROD" },
  { value: "IC385DETAIL", label: "IC385DETAIL" },
  { value: "IC385P2DETAIL", label: "IC385P2DETAIL" },
  // Phase 9.5 — TE350 is now the unified Time Clock source (replaces the
  // legacy TIME_CLOCK_YEARLY / TIME_CLOCK_MONTH split). Historical uploads
  // under those source_report values remain queryable from Efficiency.
  { value: "TE350", label: "Time Clock (TE350_*.xlsx) — Horas trabajadas del ERP" },
  // Phase 5 — catalog sources (handled by /api/catalog/ingest/*).
  { value: "SHOPIFY_CSV", label: "Shopify CSV (catálogo)" },
  { value: "IC338", label: "IC338 (inventario actual)" },
  { value: "GENQTY_AVAIL", label: "GENQTY_AVAIL (disponibilidad + imágenes)" },
];

const detectReportType = (filename) => {
  const up = (filename || "").toUpperCase();
  // Phase 9.5 — TE350 unified: any Time-Clock-shaped filename maps to TE350.
  if (
    up.includes("TE350") ||
    up.includes("TIME CLOCK") ||
    up.includes("TIME_CLOCK") ||
    up.includes("TIMECLOCK")
  ) return "TE350";
  if (up.includes("GENQTY")) return "GENQTY_AVAIL";
  if (up.includes("GENDETAILED")) return "GENDETAILED_PROD";
  if (up.includes("IC385P2")) return "IC385P2DETAIL";
  if (up.includes("IC385")) return "IC385DETAIL";
  // Phase 5
  if (up.includes("IC338")) return "IC338";
  if (up.includes("SHOPIFY") || up.includes("BASE DE SHOPIFY") || up.includes("BASE_DE_SHOPIFY")) return "SHOPIFY_CSV";
  // Phase 10.4 — accept `.zip` too. If the outer name hints at the
  // report kind we auto-detect it here; if not, the ZIP is unwrapped
  // by the backend and detection is applied to the inner filename.
  if (up.endsWith(".ZIP")) {
    // Strip the .zip suffix so filename-based detection uses the
    // "logical" name the user gave the archive (e.g. `shopify.csv.zip`
    // → `shopify.csv` → SHOPIFY_CSV).
    return detectReportType(filename.slice(0, -4));
  }
  return "";
};

const isCatalogReport = (rt) => rt === "SHOPIFY_CSV" || rt === "IC338" || rt === "GENQTY_AVAIL";

const formatNumber = (n) =>
  typeof n === "number" ? n.toLocaleString("es-MX") : n ?? "—";

export default function UploadPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [file, setFile] = useState(null);
  const [reportType, setReportType] = useState("");
  const [autoDetected, setAutoDetected] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [recent, setRecent] = useState([]);
  // Phase 5.16 — cola de archivos a inspeccionar antes de cargar
  const [inspectQueue, setInspectQueue] = useState([]);
  // Phase 10.7 — Payload of the last TE350 upload's activity_review.
  // `null` closes the review dialog; setting to an object opens it.
  const [te350Review, setTe350Review] = useState(null);
  const inputRef = useRef(null);

  const fetchRecent = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/uploads", { params: { limit: 5 } });
      setRecent(data);
    } catch (err) {
      // Intentionally swallowed: viewers don't have permission to list uploads;
      // surfacing a toast every dashboard mount would be noisy.
      console.debug("Upload.fetchRecent skipped:", err?.response?.status || err?.message);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  // Phase 5.16 — encola archivos (1+) para abrir el modal inspector.
  const handleFilesPicked = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const arr = Array.from(fileList).filter((f) => {
      const lower = f.name.toLowerCase();
      const ok =
        lower.endsWith(".xlsx") ||
        lower.endsWith(".csv") ||
        lower.endsWith(".zip"); // Phase 10.4 — accept archived uploads
      if (!ok) toast.error(`${f.name}: ${t("upload.errors.extension")}`);
      return ok;
    });
    if (arr.length === 0) return;
    setInspectQueue(arr);
    setResult(null);
    setError(null);
  };

  const onFileSelected = (f) => {
    // Compat: ruta legacy (un solo archivo); usa el modal igualmente.
    if (!f) return;
    handleFilesPicked([f]);
  };

  const handleIngest = async () => {
    if (!file) return toast.error(t("upload.errors.noFile"));
    if (!reportType) return toast.error(t("upload.errors.noType"));
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      let data;
      if (reportType === "SHOPIFY_CSV") {
        const r = await catalog.ingestShopify(file);
        data = { ...r.data, is_catalog: true, catalog_kind: "shopify" };
      } else if (reportType === "IC338") {
        const r = await catalog.ingestIc338(file);
        data = { ...r.data, is_catalog: true, catalog_kind: "ic338" };
      } else if (reportType === "GENQTY_AVAIL") {
        const r = await catalog.ingestGenqtyAvail(file);
        data = { ...r.data, is_catalog: true, catalog_kind: "genqty_avail" };
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("report_type", reportType);
        const r = await api.post("/excel/ingest", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = r.data;
      }
      setResult(data);
      toast.success(t("upload.success"));
      // Auto-trigger catalog rebuild after a catalog ingest so the user sees
      // freshly-classified data without an extra click.
      if (data.is_catalog) {
        try {
          const rb = await catalog.rebuild();
          toast.success(
            t("catalog.rebuildDone", {
              n: rb.data?.total || 0,
              s: rb.data?.elapsed_seconds || 0,
            })
          );
        } catch (rbErr) {
          console.debug("Catalog rebuild after ingest failed:", rbErr?.message);
        }
      }
      fetchRecent();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setReportType("");
    setAutoDetected(false);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div>
        <h1
          className="text-2xl font-semibold tracking-tight"
          data-testid="upload-page-title"
        >
          {t("upload.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("upload.subtitle")}</p>
      </div>

      <Card data-testid="upload-card">
        <CardContent className="space-y-4 pt-6">
          <AcceptedFileList
            type="all"
            highlight={file?.name}
            data-testid="upload-accepted-files"
          />
          <div
            data-testid="dropzone"
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              handleFilesPicked(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 transition-colors ${
              dragActive
                ? "border-primary bg-accent"
                : "border-border hover:bg-accent/40"
            }`}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            {file ? (
              <p
                className="font-mono text-sm"
                data-testid="selected-file"
              >
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            ) : (
              <>
                <p className="text-sm font-medium">{t("upload.dropzone")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("upload.dropzoneHint")}
                </p>
              </>
            )}
            <input
              ref={inputRef}
              data-testid="file-input"
              type="file"
              accept=".xlsx,.csv,.zip"
              multiple
              className="hidden"
              onChange={(e) => handleFilesPicked(e.target.files)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr,auto,auto]">
            <div>
              <Label htmlFor="report-type" className="text-xs">
                {t("upload.reportType")}{" "}
                {autoDetected && (
                  <span className="ml-1 text-emerald-500">
                    ({t("upload.reportTypeAuto")})
                  </span>
                )}
              </Label>
              <Select
                value={reportType}
                onValueChange={(v) => {
                  setReportType(v);
                  setAutoDetected(false);
                }}
              >
                <SelectTrigger
                  id="report-type"
                  data-testid="report-type-select"
                >
                  <SelectValue placeholder={t("upload.reportTypePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`report-option-${r.value}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={handleIngest}
                disabled={loading || !file || !reportType}
                data-testid="ingest-submit-btn"
                className="min-w-[180px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("upload.submitting")}
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    {t("upload.submit")}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={reset}
                disabled={loading}
                data-testid="reset-btn"
                title={t("upload.reset")}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {error && (
            <div
              className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="error-banner"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="font-mono text-xs">{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {result && result.is_catalog && (
        <div className="grid gap-4 sm:grid-cols-3" data-testid="result-cards">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Package className="h-3.5 w-3.5" />
                {result.catalog_kind === "shopify" ? "Shopify CSV" : "IC338"}
                <Badge variant="outline" className="ml-2 text-[10px]" data-testid="result-kind-catalog">
                  catalog
                </Badge>
              </CardDescription>
              <CardTitle
                className="text-3xl tabular-nums"
                data-testid="result-total"
              >
                {formatNumber(result.rows_parsed ?? result.skus_parsed ?? 0)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-emerald-500/40">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                {result.catalog_kind === "shopify" ? "Productos insertados" : "SKUs insertados"}
              </CardDescription>
              <CardTitle
                className="text-3xl tabular-nums text-emerald-500"
                data-testid="result-inserted"
              >
                {formatNumber(result.rows_inserted ?? result.skus_inserted ?? 0)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-amber-500/40">
            <CardHeader className="pb-2">
              <CardDescription>Tiempo de parseo</CardDescription>
              <CardTitle
                className="text-3xl tabular-nums text-amber-500"
                data-testid="result-elapsed"
              >
                {result.elapsed_seconds ?? 0}s
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {result && !result.is_catalog && (
        <div className="grid gap-4 sm:grid-cols-3" data-testid="result-cards">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>
                {t("upload.result.total")}
                {result.is_time_entries && (
                  <Badge variant="outline" className="ml-2 text-[10px]" data-testid="result-kind-te">
                    {t("upload.result.kindTimeEntries")}
                  </Badge>
                )}
              </CardDescription>
              <CardTitle
                className="text-3xl tabular-nums"
                data-testid="result-total"
              >
                {formatNumber(result.total_rows)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-emerald-500/40">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                {result.is_time_entries
                  ? t("upload.result.teInserted")
                  : t("upload.result.inserted")}
              </CardDescription>
              <CardTitle
                className="text-3xl tabular-nums text-emerald-500"
                data-testid="result-inserted"
              >
                {formatNumber(
                  result.is_time_entries
                    ? result.time_entries_inserted
                    : result.inserted
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-amber-500/40">
            <CardHeader className="pb-2">
              <CardDescription>
                {result.is_time_entries
                  ? t("upload.result.teDuplicates")
                  : t("upload.result.duplicates")}
              </CardDescription>
              <CardTitle
                className="text-3xl tabular-nums text-amber-500"
                data-testid="result-duplicates"
              >
                {formatNumber(
                  result.is_time_entries
                    ? result.time_entries_duplicates
                    : result.duplicates
                )}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {(result?.is_time_clock_yearly || result?.is_time_clock_month || result?.is_te350) && (
        <Alert
          className="border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30"
          data-testid="result-scope-banner"
        >
          <Target className="h-4 w-4 text-emerald-600" />
          <AlertTitle className="text-emerald-700 dark:text-emerald-300">
            {result.is_time_clock_month || (result.is_te350 && result.declared_totals_count > 0)
              ? t("scope.bannerUpdatedTitleMonth")
              : t("scope.bannerUpdatedTitle")}
          </AlertTitle>
          <AlertDescription className="text-emerald-700/80 dark:text-emerald-200/80">
            {t("scope.bannerUpdatedBody", {
              n: result.distribution_scope_count || 0,
            })}
            {result.declared_totals_count > 0 && (
              <span className="mt-1 block">
                {t("scope.bannerUpdatedDeclared", {
                  n: result.declared_totals_count,
                  from: result.period_min_date,
                  to: result.period_max_date,
                })}
              </span>
            )}
            {typeof result.filtered_no_transactions === "number" && result.filtered_no_transactions > 0 && (
              <span
                className="mt-1 block text-emerald-800/80 dark:text-emerald-100/70"
                data-testid="result-filtered-count"
              >
                {t("scope.bannerFilteredNoTx", { n: result.filtered_no_transactions })}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {isAdmin && <CoverageMatrix />}

      {isAdmin && (
        <Card data-testid="recent-uploads-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSpreadsheet className="h-4 w-4" />
              {t("upload.recent")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("uploads.empty")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("uploads.filename")}</TableHead>
                    <TableHead>{t("uploads.report")}</TableHead>
                    <TableHead className="text-right">
                      {t("uploads.rowsTotal")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("uploads.rowsInserted")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("uploads.rowsDuplicates")}
                    </TableHead>
                    <TableHead>{t("uploads.uploadedAt")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((u) => (
                    <TableRow key={u.id} data-testid={`recent-upload-${u.id}`}>
                      <TableCell className="max-w-[14rem] truncate font-mono text-xs">
                        {u.filename}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {u.source_report}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums">
                        {formatNumber(u.row_count_total)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums text-emerald-500">
                        {formatNumber(u.row_count_inserted)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums text-amber-500">
                        {formatNumber(u.row_count_duplicates)}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {u.uploaded_at?.slice(0, 19).replace("T", " ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 5.16 — Pre-upload inspect modal */}
      <UploadInspectModal
        open={inspectQueue.length > 0}
        files={inspectQueue}
        onClose={() => {
          setInspectQueue([]);
          if (inputRef.current) inputRef.current.value = "";
        }}
        onIngestComplete={(results) => {
          if (results && results.length > 0) {
            const last = results[results.length - 1].data;
            setResult(last);
            fetchRecent();
            // Phase 10.7 — Any TE350 ingest in this batch may carry an
            // `activity_review` block. We surface the LAST one so a
            // multi-file upload with several TE350s only pops one modal
            // (the freshest data wins). If the review has nothing to
            // show, skip the modal entirely.
            const te350Result = [...results].reverse().find(
              (r) =>
                r?.data?.is_te350 &&
                r?.data?.activity_review &&
                ((r.data.activity_review.to_deactivate_candidates || [])
                  .length > 0 ||
                  (r.data.activity_review.auto_reactivated || []).length > 0),
            );
            if (te350Result) {
              setTe350Review({
                activityReview: te350Result.data.activity_review,
                uploadId: te350Result.data.upload_id,
                filename: te350Result.filename || te350Result.data.filename,
              });
            }
          }
        }}
      />

      {/* Phase 10.7 — Post-TE350 activity review modal */}
      <TE350ActivityReviewDialog
        open={!!te350Review}
        onOpenChange={(v) => {
          if (!v) setTe350Review(null);
        }}
        activityReview={te350Review?.activityReview}
        uploadId={te350Review?.uploadId}
        filename={te350Review?.filename}
        onApplied={() => {
          // Refresh the recent uploads panel so the admin sees the
          // just-processed file with its final ingest stats.
          fetchRecent();
        }}
      />
    </div>
  );
}
